/**
 * Redes de los beneficiarios cripto (`medio_criptomonedas`).
 *
 * Es el mismo catálogo que usan wallet-back
 * (`recipients/beneficiario-criptomoneda/red-cripto.ts`) y Nest
 * (`medios-cripto`): el id es el de la tabla y el código es el `network` que
 * entiende el screening de wallets. Si se suma una red, se suma en los tres.
 *
 * Solo la red Tron (TRC20, id 1) la paga la integración con Binance. Las demás
 * se pagan a mano: la transacción queda en POR_PROCESAR para que la tome un
 * operador. Por eso VES→USDT, que siempre sale por Binance, ofrece únicamente
 * beneficiarios TRC20. Enviar USDT ofrece todas (30/10/2026): TRC20 sale por
 * Binance y Polygon o Solana quedan en POR_PROCESAR para pago manual.
 *
 * Además una red puede estar pausada (`activo = 0`, la apaga un operador).
 * Quien decide es el backend, que responde 422 `RED_CRIPTO_INACTIVA`: lo de
 * acá solo sirve para avisar antes y no dejar armar algo que va a rebotar.
 *
 * Sin dependencias de Angular a propósito: lo usan componentes, servicios y
 * specs por igual.
 */

export type CodigoRedCripto = 'TRC20' | 'POLYGON' | 'SOL';

export interface RedCripto {
  id: number;
  /** El `network` del screening de wallets. */
  codigo: CodigoRedCripto;
  /** Para frases y selectores: «Tron (TRC20)». */
  etiqueta: string;
  /** Para chips y columnas angostas: «TRC20». */
  etiquetaCorta: string;
  /** La `descripcion` de `medio_criptomonedas`, la que traen los listados. */
  descripcionBd: string;
  formato: RegExp;
  /** Cómo es una dirección válida, para cerrar una frase: «empieza con T y tiene 34 caracteres». */
  ayuda: string;
  placeholder: string;
  pagaBinance: boolean;
}

export const MEDIO_CRIPTO_TRC20 = 1;
export const MEDIO_CRIPTO_POLYGON = 2;
export const MEDIO_CRIPTO_SOL = 3;

/** Código del 422 con el que los dos backends rechazan una red pausada. */
export const CODIGO_RED_INACTIVA = 'RED_CRIPTO_INACTIVA';

/**
 * Los formatos (y el cruce Tron/Solana de `validarDireccion`) son los del
 * contrato común, los mismos que validan los backends. No se endurecen ni se
 * aflojan acá: una dirección que el front rechaza y el backend acepta (o al
 * revés) es un ticket seguro.
 */
export const REDES_CRIPTO: readonly RedCripto[] = [
  {
    id: MEDIO_CRIPTO_TRC20,
    codigo: 'TRC20',
    etiqueta: 'Tron (TRC20)',
    etiquetaCorta: 'TRC20',
    descripcionBd: 'RED Tron (TRC 20)',
    formato: /^T[a-zA-Z0-9]{33}$/,
    ayuda: 'empieza con T y tiene 34 caracteres',
    placeholder: 'Empieza con T (34 caracteres)',
    pagaBinance: true,
  },
  {
    id: MEDIO_CRIPTO_POLYGON,
    codigo: 'POLYGON',
    etiqueta: 'Polygon PoS',
    etiquetaCorta: 'Polygon',
    descripcionBd: 'Polygon PoS',
    formato: /^0x[a-fA-F0-9]{40}$/,
    ayuda: 'empieza con 0x y tiene 42 caracteres',
    placeholder: 'Empieza con 0x (42 caracteres)',
    pagaBinance: false,
  },
  {
    id: MEDIO_CRIPTO_SOL,
    codigo: 'SOL',
    etiqueta: 'Solana (SOL)',
    etiquetaCorta: 'Solana',
    descripcionBd: 'RED Solana (SOL)',
    formato: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    ayuda: 'tiene entre 32 y 44 letras y números, sin 0, O, I ni l',
    placeholder: 'Entre 32 y 44 caracteres',
    pagaBinance: false,
  },
];

/**
 * Una dirección Tron (T + 33) también pasa el formato de Solana, porque las
 * dos son base58. Una dirección Solana real (una clave de 32 bytes) casi
 * nunca mide 34 caracteres, así que con Solana se rechaza: cargarla así
 * mandaría a un operador a pagar a mano en la red equivocada. Mismo texto que
 * wallet-back (`motivoDireccionInvalida`).
 */
export const MENSAJE_TRON_EN_SOLANA =
  'Esa dirección es de la red Tron (TRC 20), no de Solana. Elige la red Tron o revisa la dirección.';

/**
 * Comisión de red que se informa al registrar o editar una wallet (04/11/2026):
 * un retiro a una dirección que no es de Binance paga la comisión de la red,
 * que varía; este es el valor de hoy, en USDT. Las direcciones de Binance no
 * la pagan. Solo se informa: el retiro todavía viaja con `fee: 0`.
 */
export const COMISION_RED_EXTERNA_USDT = 4;

/**
 * El id de red de un valor que puede venir vacío. Sin id —beneficiarios
 * anteriores a la columna, OTC con `wallet_trx`— es TRC20, igual que en los
 * backends. Un valor que no es número no es ninguna red conocida (NaN).
 */
export function medioCriptoDe(id: unknown): number {
  if (id === null || id === undefined || id === '') return MEDIO_CRIPTO_TRC20;
  return Number(id);
}

export function redPorId(id: unknown): RedCripto | null {
  const n = medioCriptoDe(id);
  return REDES_CRIPTO.find((r) => r.id === n) ?? null;
}

/** ¿La paga la integración con Binance? Solo TRC20 (y lo que no trae red). */
export function esRedBinance(id: unknown): boolean {
  return medioCriptoDe(id) === MEDIO_CRIPTO_TRC20;
}

/**
 * Lo mismo para el `network` guardado en un retiro de USDT, igual que
 * `retiroPagaBinance` de Nest: solo Tron, como `TRC20` (lo que mandan los
 * fronts), `TRX` (como lo nombra Binance) o sin red. Los de Polygon o Solana
 * los paga un operador a mano y no se reenvían a Binance.
 */
export function networkPagaBinance(network: unknown): boolean {
  const red = String(network ?? '').trim().toUpperCase();
  return red === '' || red === 'TRC20' || red === 'TRX';
}

/** Nombre para mostrar: el del catálogo o, si la red no está en él, la descripción que vino. */
export function etiquetaRed(id: unknown, descripcion?: string | null): string {
  return redPorId(id)?.etiqueta ?? (descripcion?.trim() || `Red ${id}`);
}

export function etiquetaCortaRed(id: unknown, descripcion?: string | null): string {
  return redPorId(id)?.etiquetaCorta ?? (descripcion?.trim() || `Red ${id}`);
}

/**
 * Motivo por el que la dirección no sirve para esa red, o `null` si está
 * bien. Mismas reglas y mismos textos que `motivoDireccionInvalida` de
 * wallet-back, para que el formulario diga lo mismo que respondería el
 * backend. Valida la dirección sin espacios en los bordes: los backends la
 * guardan recortada.
 */
export function validarDireccion(id: unknown, direccion: unknown): string | null {
  const red = redPorId(id);
  if (!red) return 'La red elegida no está disponible.';
  const valor = typeof direccion === 'string' ? direccion.trim() : '';
  if (!valor) return 'Ingresa la dirección de la wallet.';
  if (!red.formato.test(valor)) {
    return `La dirección no corresponde a ${red.descripcionBd}: ${red.ayuda}.`;
  }
  const tron = redPorId(MEDIO_CRIPTO_TRC20);
  if (red.id === MEDIO_CRIPTO_SOL && tron?.formato.test(valor)) {
    return MENSAJE_TRON_EN_SOLANA;
  }
  return null;
}

/** Lo mínimo del estado de una red (`MedioCriptoDto` de Nest). */
export interface EstadoRed {
  id: number;
  activo: boolean;
}

/**
 * ¿Está activa la red según el estado leído? Sin dato de esa red, sí: el
 * candado real es el backend, y apagarle al cliente algo que funciona por un
 * dato que no llegó sería peor que dejarlo intentar.
 */
export function redActiva(id: unknown, estado: ReadonlyArray<EstadoRed> | null | undefined): boolean {
  const n = medioCriptoDe(id);
  const fila = (estado ?? []).find((e) => Number(e?.id) === n);
  return fila ? fila.activo !== false : true;
}

/** El mismo estado con esa red apagada: lo que dijo un 422, antes de volver a leer. */
export function conRedPausada(estado: ReadonlyArray<EstadoRed> | null | undefined, id: number): EstadoRed[] {
  const lista = (estado ?? []).map((e) => (Number(e.id) === id ? { ...e, activo: false } : e));
  return lista.some((e) => Number(e.id) === id) ? lista : [...lista, { id, activo: false }];
}

/** Lo que hace falta de una fila de `recipient/usdt/beneficiario-cripto/:userId`. */
export interface BeneficiarioConRed {
  beneficiario_medio_cripto_id?: unknown;
  medioCriptomoneda_id?: unknown;
  /** 0/1, desde que wallet-back la suma al listado. */
  medioCriptomoneda_activo?: unknown;
}

export function redDeBeneficiario(b: BeneficiarioConRed | null | undefined): number {
  return medioCriptoDe(b?.beneficiario_medio_cripto_id ?? b?.medioCriptomoneda_id);
}

/**
 * ¿El beneficiario es de una red pausada?
 *
 * Basta con que lo diga una de las dos fuentes: la marca de la propia fila
 * (`medioCriptomoneda_activo`, cuando el backend ya la manda) o el estado de
 * `medios-cripto`. Si `medios-cripto` falló la marca de la fila sigue
 * sirviendo, y si el listado es de un backend anterior queda el estado. Un 422
 * también se refleja al instante con `conRedPausada`, aunque la fila diga otra
 * cosa.
 */
export function beneficiarioPausado(
  b: BeneficiarioConRed | null | undefined,
  estado: ReadonlyArray<EstadoRed> | null | undefined,
): boolean {
  const marca = b?.medioCriptomoneda_activo;
  const marcaApagada = marca === 0 || marca === '0' || marca === false;
  return marcaApagada || !redActiva(redDeBeneficiario(b), estado);
}

/** «A», «A y B», «A, B y C». */
export function unirNombres(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/**
 * Texto de la franja que explica qué beneficiarios se pueden usar, o `null`
 * si todas las redes que importan en la pantalla están activas.
 *
 * `relevantes` son las redes que la pantalla ofrece: todas en la calculadora
 * CLP→USDT y en Enviar USDT, solo TRC20 en VES→USDT. Una red apagada que la
 * pantalla igual no ofrece no merece un cartel.
 */
export function avisoRedesPausadas(
  estado: ReadonlyArray<EstadoRed> | null | undefined,
  relevantes: number[] = REDES_CRIPTO.map((r) => r.id),
): string | null {
  const redes = relevantes
    .map((id) => redPorId(id))
    .filter((r): r is RedCripto => r !== null);
  const pausadas = redes.filter((r) => !redActiva(r.id, estado));
  if (pausadas.length === 0) return null;

  const activas = redes.filter((r) => redActiva(r.id, estado));
  const cuales =
    pausadas.length === 1
      ? `La red ${pausadas[0].etiqueta} está pausada por el momento.`
      : `Las redes ${unirNombres(pausadas.map((r) => r.etiqueta))} están pausadas por el momento.`;

  if (activas.length === 0) {
    return `${cuales} Por ahora no puedes enviar a beneficiarios cripto; vuelve a intentarlo más tarde.`;
  }
  return activas.length === 1
    ? `${cuales} Por ahora solo puedes usar beneficiarios de la red activa: ${activas[0].etiqueta}.`
    : `${cuales} Por ahora solo puedes usar beneficiarios de las redes activas: ${unirNombres(activas.map((r) => r.etiqueta))}.`;
}

/**
 * ¿El backend rechazó porque la red está pausada?
 *
 * Acepta las dos formas en que puede llegar: el `HttpErrorResponse` que el
 * `ErrorInterceptor` deja intacto en un 422 (`error.error.code`) y el error ya
 * traducido por `errorHttpReal` (`codigo`), que es lo que reciben las
 * pantallas del wallet VES.
 */
export function esErrorRedInactiva(error: any): boolean {
  return error?.error?.code === CODIGO_RED_INACTIVA || error?.codigo === CODIGO_RED_INACTIVA;
}

/** El mensaje del 422 (el backend nombra la red), o uno genérico si no vino. */
export function mensajeRedInactiva(error: any): string {
  const delBackend = error?.error?.message ?? error?.message;
  if (typeof delBackend === 'string' && delBackend.trim()) return delBackend.trim();
  return 'La red de este beneficiario está pausada por el momento. Por ahora solo puedes usar beneficiarios de las redes activas.';
}
